/**
 * SSC-108: item weights in the admin form.
 *
 * The API stores the base unit's weights as integer milligrams. Operators
 * enter grams with up to three decimals, in their locale's decimal separator.
 * Both directions work on the digits as text, so no value passes through a
 * float: 0.1 g is 100 mg exactly, not 99.99999.
 */

/** Largest value the API's INTEGER columns hold. */
const MAX_WEIGHT_MG = 2_147_483_647;

/**
 * Grams text → milligrams. Blank is `null` (no weight); `undefined` means the
 * text is not a valid weight: not a decimal, zero, more than three decimals,
 * or too large. `.` and `,` are both accepted as the decimal separator.
 */
export function gramsToMg(text: string): number | null | undefined {
  const trimmed = text.trim().replace(/\s/g, '');
  if (trimmed === '') return null;
  const match = /^(\d+)(?:[.,](\d{0,3}))?$/.exec(trimmed);
  if (match === null) return undefined;
  const mg = BigInt((match[1] ?? '0') + (match[2] ?? '').padEnd(3, '0'));
  if (mg === 0n || mg > BigInt(MAX_WEIGHT_MG)) return undefined;
  return Number(mg);
}

/** The decimal separator the locale writes (`.` for en, `,` for lt and uk). */
export function decimalSeparator(locale: string): string {
  try {
    const part = new Intl.NumberFormat(locale)
      .formatToParts(1.5)
      .find((entry) => entry.type === 'decimal');
    return part?.value ?? '.';
  } catch {
    return '.';
  }
}

/**
 * Milligrams → grams text for the locale, shortest exact form, no grouping
 * (so the operator can edit it and it parses back): 1500 → `1.5` / `1,5`.
 * `null` renders as an empty field.
 */
export function mgToGramsText(mg: number | null, locale: string): string {
  if (mg === null) return '';
  const whole = Math.trunc(mg / 1000);
  const fraction = String(mg % 1000)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return fraction === '' ? String(whole) : `${whole}${decimalSeparator(locale)}${fraction}`;
}
